import { useIsFocused } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAxiosClient } from "@/hooks/useAxiosClient";
import { useRegion } from "@/hooks/useRegion";
import {
  GetAdminAccountPanelsRequest,
  GetAdminClientsRequest,
  GetAdminUsageRequest,
} from "@/types/requests";
import { AdminClient, AdminUsage, Panel, Share } from "@/types/resources";
import {
  GetAdminAccountPanelsResponse,
  GetAdminClientsResponse,
  GetAdminUsageResponse,
} from "@/types/responses";

const PANELS_PER_PAGE = 20;

export const useAdminAccountPanels = (
  accountType: "orgs" | "users",
  accountId: string
) => {
  const [count, setCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [panels, setPanels] = useState<Panel[]>([]);
  const [shares, setShares] = useState<Share[]>([]);

  const client = useAxiosClient("/admin");

  const getPanels = useCallback(
    (p: number) => {
      const params: GetAdminAccountPanelsRequest = {
        items_per_page: PANELS_PER_PAGE,
        page: p,
      };

      client
        .get<GetAdminAccountPanelsResponse>(
          `/${accountType}/${accountId}/panels`,
          { params }
        )
        .then(({ data }) => {
          setCount(data.count);
          setPanels((prev) =>
            p === 1 ? data.panels : [...prev, ...data.panels]
          );
          setShares((prev) =>
            p === 1 ? data.shares : [...prev, ...data.shares]
          );
          setError(null);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : "Unknown error");
        })
        .finally(() => {
          if (p === 1) setLoading(false);
          else setLoadingMore(false);
        });
    },
    [accountId, accountType, client]
  );

  useEffect(() => {
    getPanels(1);
  }, [getPanels]);

  const loadMore = useCallback(() => {
    const next = page + 1;
    setLoadingMore(true);
    setPage(next);
    getPanels(next);
  }, [getPanels, page]);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    setPage(1);
    getPanels(1);
  }, [getPanels]);

  return {
    count,
    error,
    hasMore: panels.length < count,
    loading,
    loadingMore,
    loadMore,
    panels,
    refresh,
    shares,
  };
};

export const useAdminUsage = (
  startDate: Date,
  endDate: Date,
  userId?: string
) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState<AdminUsage | null>(null);

  const client = useAxiosClient("/admin");
  const { region } = useRegion();
  const startIso = startDate.toISOString();
  const endIso = endDate.toISOString();

  const fetch = useCallback(() => {
    const params: GetAdminUsageRequest = {
      end_date: endIso,
      start_date: startIso,
      ...(userId ? { user_id: userId } : {}),
    };

    client
      .get<GetAdminUsageResponse>("/usage", { params })
      .then(({ data }) => {
        setUsage(data.usage);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Unknown error");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client, endIso, startIso, userId]);

  // Re-enter the loading state during render (not in an effect) when the
  // query inputs change, so the UI shows loading while refetching.
  const inputsKey = JSON.stringify([startIso, endIso, userId, region]);
  const [prevInputsKey, setPrevInputsKey] = useState(inputsKey);
  if (prevInputsKey !== inputsKey) {
    setPrevInputsKey(inputsKey);
    setLoading(true);
    setError(null);
  }

  // Requests are routed to the backend of the region carried by the session
  // token, so a region switch made on another tab refetches once back here.
  const isFocused = useIsFocused();
  const fetchedKey = useRef<string | null>(null);
  useEffect(() => {
    if (!isFocused || fetchedKey.current === inputsKey) return;
    fetchedKey.current = inputsKey;
    fetch();
  }, [fetch, inputsKey, isFocused]);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch();
  }, [fetch]);

  return { error, loading, refresh, usage };
};

export const useAdminClients = (startDate: Date, endDate: Date) => {
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const client = useAxiosClient("/admin");
  const { region } = useRegion();
  const startIso = startDate.toISOString();
  const endIso = endDate.toISOString();

  const fetch = useCallback(() => {
    const params: GetAdminClientsRequest = {
      end_date: endIso,
      start_date: startIso,
    };

    client
      .get<GetAdminClientsResponse>("/clients", { params })
      .then(({ data }) => {
        setClients(data.clients);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Unknown error");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client, endIso, startIso]);

  // Re-enter the loading state during render (not in an effect) when the
  // query inputs change, so the UI shows loading while refetching.
  const inputsKey = JSON.stringify([startIso, endIso, region]);
  const [prevInputsKey, setPrevInputsKey] = useState(inputsKey);
  if (prevInputsKey !== inputsKey) {
    setPrevInputsKey(inputsKey);
    setLoading(true);
    setError(null);
  }

  // See useAdminUsage.
  const isFocused = useIsFocused();
  const fetchedKey = useRef<string | null>(null);
  useEffect(() => {
    if (!isFocused || fetchedKey.current === inputsKey) return;
    fetchedKey.current = inputsKey;
    fetch();
  }, [fetch, inputsKey, isFocused]);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch();
  }, [fetch]);

  const regionClients = useMemo(
    () => clients.filter((c) => c.region === region),
    [clients, region]
  );

  return { clients: regionClients, error, loading, refresh };
};
